// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Tab from "@insite/mobius/Tab/Tab";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@steiner/App/Components/Common/XngZone";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import TabGroup, { TabGroupPresentationProps } from "@steiner/MobiusOverrides/TabGroup/TabGroup";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";

interface TabsStyles {
    tabsContainer?: TabGroupPresentationProps;
    wrapper?: InjectableCss;
    container?: InjectableCss;
    title?: TypographyPresentationProps;
}

export const tabsStyles: TabsStyles = {};

const styles = tabsStyles;

const enum tabFields {
    tabName = "tabName",
}

const enum fields {
    title = "title",
    tabs = "tabs",
}

interface TabField {
    fields: {
        [tabFields.tabName]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabs]: TabField[];
    };
}
type Props = OwnProps;

const XTabs: FC<Props> = ({ fields: { tabs = [], title }, id }) => {
    const isEditing = useShellContext().isEditing;
    const tabContainerRef = useRef<HTMLDivElement>(null);
    const [tabsState, setTabsState] = useState<TabField[]>([]);
    const _tabsState = useAppSelector((state: ApplicationState) => (state as CustomAppState).xTabs);

    useEffect(() => {
        setTabsState(tabs);
    }, [isEditing, tabs]);
    const isVisibleTab = (tab: TabField) => {
        const tabPanel = tabContainerRef.current?.querySelector(`[id="tab_${tab.fields.tabName}"]`); // handle non-loading zones
        return !!_tabsState[`tab_${tab.fields.tabName}`] || !!tabPanel?.children?.[0]?.children?.length;
    };

    const atLeastOneVisible = useMemo(() => !!tabsState.find(tab => isVisibleTab(tab)), [_tabsState]);

    return (
        <StyledWrapper {...styles.wrapper} ref={tabContainerRef} key={`tabsWrapper_${tabsState.length}`}>
            {!!tabsState.length && (
                <StyledWrapper {...styles.container}>
                    {atLeastOneVisible && !!title && <Typography {...styles.title}>{title}</Typography>}
                    <TabGroup {...styles.tabsContainer}>
                        {tabsState.map(tab => (
                            <Tab
                                key={`${tab.fields.tabName}`}
                                headline={tab.fields.tabName}
                                tabKey={tab.fields.tabName}
                                aria-label={`${tab.fields.tabName} tab`}
                                hidden={!isEditing && !isVisibleTab(tab)}
                            >
                                <XngZone
                                    contentId={id}
                                    zoneName={`tab_${tab.fields.tabName}`}
                                    hint={`Content for ${tab.fields.tabName}`}
                                />
                            </Tab>
                        ))}
                    </TabGroup>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
const xTabWidget: WidgetModule = {
    component: XTabs,
    definition: {
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                editorTemplate: "ListField",
                name: fields.tabs,
                getDisplay: (item: any) => item.fields.tabName,
                defaultValue: [],
                fieldType: "General",
                isRequired: true,
                fieldDefinitions: [
                    {
                        name: tabFields.tabName,
                        editorTemplate: "TextField",
                        displayName: "Tab Name",
                        isRequired: true,
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default xTabWidget;
